import {
  Button,
  Card,
  DatePicker,
  Grid,
  Modal,
  Row,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CardTitle, ColumnComponent } from "../../components/ui";
import QuickbooksContext from "../../context/QuickbooksContext";
import Table from "antd/es/table";
import dayjs from "dayjs";
import DataContext from "../../context/DataContext";
import { useParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import { DownloadOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { MultipleSelect, SearchInput } from "../../components/functional";
import { USDollar } from "../../utils/func/currency";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue } from "antd/es/table/interface";
import { TableProps } from "antd/lib";
import { useDemoModal } from "../../hooks";
import * as htmlToImage from "html-to-image";
import * as XLSX from "xlsx";
import { InvoiceDrawer } from "../../components/collections/InvoiceDrawer";
import { getInvPaymentsColumns } from "../../components/utils";
import {
  CustomerGroup,
  flattenCustomerDetails,
  getDetailColumns,
} from "../../components/utils/columns/invPaymentsColumns";

const { Text } = Typography;
const { RangePicker } = DatePicker;

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const InvoicesReceived = () => {
  const { idClient } = useParams();
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const { clientInfo, isDemoEnv } = useContext(AuthContext);
  const { DemoModal, openModalDemo } = useDemoModal();
  const [sortedInfo] = useState<Sorts>({});
  const {
    qbLoading,
    isInvoiceDrawerOpen,
    invoiceSelected,
    invoiceListSelected,
    indexInvoiceSelected,
    handleCancelSelectInvoiceList,
    handleSelectInvoice,
    handleSelectInvoiceList,
    updateSelectedInvoice,
    getData,
    onChangeSearchQuery,
  } = useContext(QuickbooksContext);

  const {
    getData: getListData,
    customerOptions,
    paymentMethodOptions,
    handleSuccess,
  } = useContext(DataContext);

  const [reportData, setReportData] = useState<any>({
    data: [],
    total: { invoiced: 0, applied: 0, remaining: 0 },
  });
  const [customerSelected, setCustomerSelected] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentMethodSelected, setPaymentMethodSelected] = useState<string[]>(
    [],
  );
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);

  const [internalLoading, setInternalLoading] = useState(false);
  const screenRef = useRef<HTMLDivElement>(null);

  // ---- initial fetch
  useEffect(() => {
    if (!idClient) return;
    getListData("customer", idClient);
    getListData("payment-methods", idClient);
    fetchData();
  }, [idClient]);

  const fetchData = (overrides?: {
    customer?: string[];
    paymentMethod?: string[];
    start?: string | null;
    end?: string | null;
  }) => {
    if (!idClient) return;
    const finalCustomerSelected = overrides?.customer ?? customerSelected;
    const finalPaymentMethodSelected =
      overrides?.paymentMethod ?? paymentMethodSelected;
    const finalDateRange = dateRange;

    getData({
      endpoint: "invoices-received", // new endpoint
      idClient,
      filters: {
        customer:
          finalCustomerSelected.length > 0 ? finalCustomerSelected : null,
        status: null,
        paymentMethod:
          finalPaymentMethodSelected.length > 0
            ? finalPaymentMethodSelected
            : null,
        start:
          overrides?.start ??
          (finalDateRange?.[0] ? finalDateRange[0].format("YYYY-MM-DD") : null),
        end:
          overrides?.end ??
          (finalDateRange?.[1] ? finalDateRange[1].format("YYYY-MM-DD") : null),
      },
      next: (payload: any) => setReportData(payload),
    });
  };

  const columns = useMemo(
    () =>
      getInvPaymentsColumns({
        onInvoiceCellClick: () => handleSelectInvoiceList,
      }),
    [handleSelectInvoiceList],
  );

  const detailCols = useMemo(
    () =>
      getDetailColumns({
        onOpenInvoice: () => handleSelectInvoiceList,
      }),
    [handleSelectInvoiceList],
  );

  const handleScreenshot = async () => {
    if (!screenRef.current) return;
    setInternalLoading(true);
    setTimeout(() => {
      htmlToImage
        .toBlob(screenRef.current!)
        .then(async function (blob) {
          if (!blob) return;
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          handleSuccess(true, "Screenshot copied to clipboard");
        })
        .catch(() => handleSuccess(false, "Failed to copy screenshot"))
        .finally(() => setInternalLoading(false));
    }, 300);
  };

  const handleExport = () => {
    const rows: any[] = [];

    // Export structure mirrors the screen display:
    // 1. Customer summary rows (main table)
    // 2. Detailed transaction rows (expanded table)

    reportData.data.forEach((cust: CustomerGroup) => {
      // Add customer summary row (matches main table columns)
      rows.push({
        RowType: "Customer Summary",
        Customer: cust?._id?.name || "-",
        "# Invoices": cust?.invoices?.length ?? 0,
        Invoiced: cust?.total?.invoiced || 0,
        Applied: cust?.total?.applied || 0,
        Remaining: Math.max(0, cust?.total?.remaining || 0),
        // "Invoice #": "",
        Date: "",
        "Transaction Type": "",
        "Memo/Description": "",
        Num: "",
        "Payment Method": "",
        "Tracking Number": "",
        Amount: "",
      });

      // Add detailed transaction rows for this customer (matches expanded table)
      const detailRows = flattenCustomerDetails(cust);
      detailRows.forEach((detail) => {
        rows.push({
          RowType: "Transaction Detail",
          Customer: cust?._id?.name || "-",
          "# Invoices": "",
          Invoiced: "",
          Applied: "",
          Remaining: "",
          // "Invoice #": detail.invoiceNumber || "-",
          Date: detail.date ? dayjs(detail.date).format("MM/DD/YYYY") : "-",
          "Transaction Type": detail.transactionType,
          "Memo/Description": detail.memo || "",
          Num: detail.num || "",
          "Payment Method": detail.paymentMethod || "",
          "Tracking Number": detail.trackingNumber || "",
          Amount: detail.amount || 0,
        });
      });

      // Add empty row for separation between customers
      rows.push({
        RowType: "",
        Customer: "",
        "# Invoices": "",
        Invoiced: "",
        Applied: "",
        Remaining: "",
        // "Invoice #": "",
        Date: "",
        "Transaction Type": "",
        "Memo/Description": "",
        Num: "",
        "Payment Method": "",
        "Tracking Number": "",
        Amount: "",
      });
    });

    // Add totals row at the end (matches table summary)
    rows.push({
      RowType: "Grand Total",
      Customer: "TOTAL",
      "# Invoices": "",
      Invoiced: reportData.total?.invoiced || 0,
      Applied: reportData.total?.applied || 0,
      Remaining: reportData.total?.remaining || 0,
      // "Invoice #": "",
      Date: "",
      "Transaction Type": "",
      "Memo/Description": "",
      Num: "",
      "Payment Method": "",
      "Tracking Number": "",
      Amount: "",
    });

    // Generate XLSX file using the same pattern as ExportFile.ts
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices & Payments");
      XLSX.writeFile(
        workbook,
        `Invoices & Received Payments - ${dayjs().format("MM.DD.YYYY")}.xlsx`,
      );
    } catch (e) {
      console.error("Export failed:", e);
    }
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardTitle
            title={clientInfo?.name + " - Invoices & Received Payments"}
          />
        </ColumnComponent>

        <ColumnComponent>
          <Card
            styles={{ body: { display: "none" } }}
            className="summary-client__card"
            actions={[
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Actions</label>
                <Space direction="horizontal" size={0}>
                  <Tooltip title="Export Excel" placement="top">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      size="large"
                      onClick={handleExport}
                    />
                  </Tooltip>
                </Space>
              </Space>,
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Filter by Customer</label>
                <Tooltip title="Filter By Customer">
                  <MultipleSelect
                    placeholder="Customer"
                    className="summary-client__card__actions__select"
                    options={customerOptions}
                    onSelect={(values) => {
                      const newCustomerSelected = values as string[];
                      setCustomerSelected(newCustomerSelected);
                      fetchData({ customer: newCustomerSelected });
                    }}
                  />
                </Tooltip>
              </Space>,
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Filter By Pay. Method</label>
                <MultipleSelect
                  placeholder="Payment Method"
                  className="summary-client__card__actions__select"
                  options={paymentMethodOptions}
                  onSelect={(values) => {
                    const newPaymentMethodSelected = values as string[];
                    setPaymentMethodSelected(newPaymentMethodSelected);
                    fetchData({ paymentMethod: newPaymentMethodSelected });
                  }}
                />
              </Space>,
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Date Range</label>
                <RangePicker
                  // className="summary-client__card__actions__select"
                  style={{ width: "80%" }}
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates);
                    // Use the dates parameter directly instead of waiting for state update
                    if (dates) {
                      const start = dates[0]
                        ? dates[0].format("YYYY-MM-DD")
                        : null;
                      const end = dates[1]
                        ? dates[1].format("YYYY-MM-DD")
                        : null;
                      fetchData({ start, end });
                    } else {
                      // Handle case when date range is cleared
                      fetchData({ start: null, end: null });
                    }
                  }}
                  placeholder={["Start Date", "End Date"]}
                />
              </Space>,
              // <Space direction="vertical" size={0} style={{ width: "100%" }}>
              //   <label>Search</label>
              //   <SearchInput
              //     style={{ width: "80%" }}
              //     onSearch={(value: string) => {
              //       onChangeSearchQuery("");
              //       setSearchQuery(value);
              //       if (!idClient) return;
              //       if (value) {
              //         getData({
              //           endpoint: "invoices-received",
              //           idClient,
              //           search: value,
              //           filters: {
              //             customer:
              //               customerSelected.length > 0
              //                 ? customerSelected
              //                 : null,
              //             status: null,
              //             paymentMethod:
              //               paymentMethodSelected.length > 0
              //                 ? paymentMethodSelected
              //                 : null,
              //             start: dateRange?.[0]
              //               ? dateRange[0].format("YYYY-MM-DD")
              //               : null,
              //             end: dateRange?.[1]
              //               ? dateRange[1].format("YYYY-MM-DD")
              //               : null,
              //           },
              //           next: (payload: any) => setReportData(payload),
              //         });
              //       } else {
              //         fetchData();
              //       }
              //     }}
              //   />
              // </Space>,
            ]}
          />
        </ColumnComponent>

        <ColumnComponent>
          <div ref={screenRef} id="screenshot">
            {qbLoading ? (
              <div style={{ minHeight: "25vw" }}>
                <Space align="center" direction="vertical">
                  <Spin size="large" />
                </Space>
              </div>
            ) : (
              <Table
                bordered
                columns={columns}
                dataSource={reportData.data as CustomerGroup[]}
                className="summary-client__table"
                pagination={{
                  pageSize: 50,
                  total: reportData.data?.length ?? 0,
                }}
                size="small"
                rowKey={"idClient"}
                scroll={{ y: "70vh", x: "100%" }}
                tableLayout="auto"
                // NEW: expanded rows render a nested Table with the 7 columns
                expandable={{
                  expandedRowRender: (record: CustomerGroup) => (
                    <Table
                      columns={detailCols}
                      dataSource={flattenCustomerDetails(record)}
                      pagination={false}
                      size="small"
                      rowKey="key"
                      bordered
                    />
                  ),
                }}
                summary={() => (
                  <Table.Summary.Row className="table-row">
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {USDollar.format(reportData.total?.invoiced || 0)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {USDollar.format(reportData.total?.applied || 0)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {USDollar.format(reportData.total?.remaining || 0)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            )}
          </div>
        </ColumnComponent>

        <ColumnComponent>
          <div style={{ textAlign: "right" }}>
            <Tooltip title="Help">
              <Button
                icon={<QuestionCircleOutlined style={{ color: "#17549a" }} />}
                type="default"
                onClick={() => {
                  Modal.info({
                    title: "Invoices & Received Payments",
                    content:
                      "Shows each invoice with payments applied within the selected date range. Click an invoice to open the Collections drawer.",
                  });
                }}
              />
            </Tooltip>
          </div>
        </ColumnComponent>
      </Row>

      {/* Side Drawer (reused) */}
      <InvoiceDrawer
        title={`Collections (${invoiceSelected?.TransactionType || "Invoice"} ${
          (indexInvoiceSelected ?? 0) + 1
        } of ${invoiceListSelected.length})`}
        open={isInvoiceDrawerOpen}
        onClose={handleCancelSelectInvoiceList}
        invoice={invoiceSelected}
        options={[]} // or statusList if you want editing here too
        idClient={idClient}
        indexInvoiceSelected={indexInvoiceSelected}
        list={invoiceListSelected}
        selectInvoice={(i) => handleSelectInvoice(i)}
        onClickNext={
          indexInvoiceSelected! < invoiceListSelected.length - 1
            ? () => handleSelectInvoice(indexInvoiceSelected! + 1)
            : undefined
        }
        onClickPrevious={
          indexInvoiceSelected! > 0
            ? () => handleSelectInvoice(indexInvoiceSelected! - 1)
            : undefined
        }
        onSubmit={(newNote, emailAddress, phoneNumber, newDate, next) => {
          if (isDemoEnv) {
            openModalDemo();
          } else if (idClient) {
            updateSelectedInvoice(
              idClient,
              { newNote, emailAddress, phoneNumber, newDate },
              () => next && next(),
            );
          }
        }}
      />

      <DemoModal />
    </>
  );
};
